import {ADMIN_PAGES, ADMIN_ROUTE, adminPagePath, CAMPAIGN_DRAFT_PATH, SENT_CAMPAIGN_PATH} from "account/admin/adminRoute";
import {treeRelativePath} from "seo/documentLang";

/**
 * Which route PATTERN a pathname belongs to (kitecorp/transferify-ui#2581).
 *
 * ## Why a pattern and never the pathname
 *
 * `/download/<capability>` is a working share link, and `/reminder-uploads/<reminderId>/<contactId>` names
 * two of a sender's records. An error report that carried the resolved pathname would put those into a log
 * an operator can read — so what goes on the wire is the PATTERN the reader was on, which is code rather
 * than data. The api enforces the same thing independently with a grammar that cannot express an identifier
 * (`ClientErrorReportRequest.ROUTE_PATTERN`), so a mistake here is a refused report rather than a leak.
 *
 * ## Why it fails safe
 *
 * An unrecognised pathname answers {@link CLIENT_ERROR_UNKNOWN_ROUTE}, never the pathname itself. That is
 * what makes the privacy property independent of this list being complete: a route added to `routes.tsx`
 * and not here reports as `/other` and costs some precision, rather than leaking. The list still has to
 * keep up, which is why `clientErrorRoute.routes.test.ts` reads `src/routes.tsx` and fails when it declares
 * a path no pattern here covers.
 *
 * ## Why the locale prefix is dropped
 *
 * `/ro/pricing` and `/pricing` are one route. The language is already its own field on the report, so
 * keeping the prefix would split every pattern three ways and say nothing new. What a prefix is comes from
 * `seo/documentLang.treeRelativePath`, the one module that decides what `/ro` means.
 *
 * ## The content corpus
 *
 * The corpus pages (`src/content/**`, `/guide/<slug>`, `/send-large-files`, …) are route records spread in
 * from the content index, not literal paths, so the table below cannot list them. The content boundary is
 * attached only to those records, so {@link contentErrorRouteOf} names what it catches by shape instead.
 *
 * React-free by mandate (`logic-outside-react`). It cannot import `routes.tsx` — that module pulls in React
 * — which is exactly why the test above compares the two as text.
 */

/** The answer for a pathname no pattern matches. Admitted by the api's route grammar. */
export const CLIENT_ERROR_UNKNOWN_ROUTE = "/other";

/**
 * Every route pattern the app declares, as the api's grammar spells one: lower-case words and `:name`
 * placeholders, no `?`. An optional segment is listed as BOTH of its shapes, because the report says which
 * one the reader was actually on and "one or two ids" is not a route.
 *
 * Sorted longest-first so a two-segment pattern cannot be shadowed by a one-segment one that happens to
 * share its head.
 *
 * The admin console's paths are read from `account/admin/adminRoute`, which owns them, rather than typed
 * here (kitecorp/transferify-ui#3830): each page is a route of its own, so a report says which page failed,
 * and a page added to the console is covered the day it is added. The campaign steps and one sent campaign
 * are routes under the campaigns page; a sent campaign's key is in its query, which no pattern reads.
 */
export const CLIENT_ERROR_ROUTE_PATTERNS: readonly string[] = [
	CAMPAIGN_DRAFT_PATH,
	SENT_CAMPAIGN_PATH,
	...ADMIN_PAGES.map(adminPagePath),
	ADMIN_ROUTE,
	"/activate/:token",
	"/download/:id",
	"/reminder-uploads/:reminderId/:contactId",
	"/reminder-uploads/:reminderId",
	"/transfers/files",
	"/transfers/requested",
	"/unsubscribe/offers",
	"/about",
	"/account",
	"/advertise",
	"/advertising",
	"/advertising-terms",
	"/cancel-contract",
	"/client-server",
	"/contact",
	"/contacts",
	"/cookies",
	"/data-deletion",
	"/features",
	"/impressum",
	"/login",
	"/p2p",
	"/personalization",
	"/pricing",
	"/privacy",
	"/reminders",
	"/sponsor",
	"/terms",
	"/transfers",
	"/unsubscribe",
	"/zero-knowledge",
	"/",
];

/** The pathname's segments inside its locale tree: `/ro/pricing` and `/pricing` are both `["pricing"]`. */
function localeFreeSegments(pathname: string): string[] {
	return treeRelativePath(pathname).split("/").filter((segment) => segment !== "");
}

/** Whether a pattern's segments describe these pathname segments, treating `:name` as any one segment. */
function patternMatches(pattern: string, segments: readonly string[]): boolean {
	const patternSegments = pattern.split("/").filter((segment) => segment !== "");
	if (patternSegments.length !== segments.length) {
		return false;
	}
	return patternSegments.every((patternSegment, index) =>
		patternSegment.startsWith(":")
			// A placeholder matches any non-empty segment. Its CONTENT is never read, which is the point:
			// nothing about the value reaches the answer.
			? segments[index] !== ""
			: patternSegment === segments[index].toLowerCase());
}

/**
 * The route pattern for a pathname, or {@link CLIENT_ERROR_UNKNOWN_ROUTE}.
 *
 * @param pathname `window.location.pathname`, or any path the caller has
 */
export function clientErrorRouteOf(pathname: string): string {
	const segments = localeFreeSegments(pathname);
	if (segments.length === 0) {
		// Both `/` and a bare locale tree (`/ro`) are the home route.
		return "/";
	}
	return CLIENT_ERROR_ROUTE_PATTERNS.find((pattern) => patternMatches(pattern, segments))
		?? CLIENT_ERROR_UNKNOWN_ROUTE;
}

/**
 * One segment of the api's route grammar (`ClientErrorReportRequest.ROUTE_PATTERN`): a lower-case word of
 * at most 24 characters starting with a letter. A section that does not fit reports as the unknown route
 * rather than a pattern the api would refuse.
 */
const ROUTE_SEGMENT = /^[a-z][a-z0-9-]{0,23}$/;

/** What a corpus page at the root of its tree reports as: its slug is its name, so it is a placeholder. */
export const CONTENT_ROOT_ARTICLE_ROUTE = "/:article";

/**
 * The route pattern of a page the content boundary caught.
 *
 * That boundary is attached only to the corpus records (`routes.tsx`, `contentRouteRecords`), and every
 * corpus page sits either at the root of its tree (`/send-large-files`) or one level into a section
 * (`/guide/<slug>`, `/ro/comparatie/<slug>`). So the answer is the section with the slug as a placeholder
 * (`/guide/:slug`), or {@link CONTENT_ROOT_ARTICLE_ROUTE}. The slug never reaches the wire. The section does,
 * and it is a directory of the corpus rather than a value a reader typed, because this boundary renders only
 * for a route the corpus declared. Any other shape answers what {@link clientErrorRouteOf} answers.
 *
 * @param pathname `window.location.pathname`
 */
export function contentErrorRouteOf(pathname: string): string {
	const segments = localeFreeSegments(pathname);
	if (segments.length === 1) {
		return CONTENT_ROOT_ARTICLE_ROUTE;
	}
	if (segments.length === 2) {
		const section = segments[0].toLowerCase();
		return ROUTE_SEGMENT.test(section) ? `/${section}/:slug` : CLIENT_ERROR_UNKNOWN_ROUTE;
	}
	return clientErrorRouteOf(pathname);
}
